import { useMemo } from "react";

import type { City, GroupedStations } from "@repo/ui/types/stations";
import { getAvailableDepartures, getAvailableDestinations } from "@repo/ui/utils/transformData";

export function useFilteredStations(
  destinations: GroupedStations,
  departureValue: string,
  destinationValue: string,
  disableFilter: boolean = false,
): {
  allStations: City[];
  departureCode: string | null;
  destinationCode: string | null;
  filteredDepartures: GroupedStations;
  filteredDestinations: GroupedStations;
} {
  const allStations: City[] = useMemo(() => Object.values(destinations).flat(), [destinations]);

  // console.log("allStations", allStations);

  const departureCode = useMemo(() => {
    const fromParens = departureValue?.match(/\(([^)]+)\)/)?.[1];
    const raw = (fromParens ?? departureValue ?? "").trim().toUpperCase();
    return raw || null;
  }, [departureValue]);

  const destinationCode = useMemo(() => {
    const fromParens = destinationValue?.match(/\(([^)]+)\)/)?.[1];
    const raw = (fromParens ?? destinationValue ?? "").trim().toUpperCase();
    return raw || null;
  }, [destinationValue]);

  const filteredDestinations = useMemo(() => {
    if (!departureCode) return destinations;
    return getAvailableDestinations(allStations, departureCode);
  }, [departureCode, allStations, destinations]);

  const filteredDepartures = useMemo(() => {
    if (disableFilter) return destinations;
    if (!destinationCode) return destinations;
    return getAvailableDepartures(allStations, destinationCode);
  }, [disableFilter, destinations, destinationCode, allStations]);

  return {
    allStations,
    departureCode,
    destinationCode,
    filteredDepartures,
    filteredDestinations,
  };
}
